/**
 * Users › Admin Users on a phone (SAM-1308, canvas MobileLists M1): the same page as the other Users tabs: a title with the one
 * primary action, a full-width search, the tab chips, and a card per user with the pager under the list. Presentational:
 * AdminUsers.tsx owns the data, the search and the sheets.
 */
import React from 'react';
import { PhoneListState, PhonePager } from '../../components/phone/PhoneCards';
import {
  PhoneCapsules, PhoneItemList, PhonePage, PhoneSearch, PhoneTitleRow, type PhoneCapsule, type PhoneItem,
} from '../../components/phone/PhoneListParts';
import { initialsOf } from '../../components/phone/PhoneCards';
import { useT } from '../../i18n/admin';
import type { Tone } from '../BusinessPage/activity';
import type { SAMUser } from '../../types/role.types';

const STATUS_TONE: Record<string, Tone> = { CONFIRMED: 'ok', FORCE_CHANGE_PASSWORD: 'wait' };
const STATUS_KEYS = ['CONFIRMED', 'FORCE_CHANGE_PASSWORD', 'UNCONFIRMED'] as const;

export const ADMIN_USERS_PHONE_LIMIT = 20;

interface Props {
  title: string;
  chips: PhoneCapsule[];
  onChip: (id: string) => void;
  users: SAMUser[];
  loading: boolean;
  query: string;
  onQuery: (q: string) => void;
  page: number;
  onPage: (page: number) => void;
  onOpen: (user: SAMUser) => void;
  onCreate: () => void;
  onOtherRole: () => void;
}

export const AdminUsersPhone: React.FC<Props> = ({ title, chips, onChip, users, loading, query, onQuery, page, onPage, onOpen, onCreate, onOtherRole }) => {
  const { t } = useT();
  const pageUsers = users.slice((page - 1) * ADMIN_USERS_PHONE_LIMIT, page * ADMIN_USERS_PHONE_LIMIT);
  const items: PhoneItem[] = pageUsers.map((u) => {
    const name = [u.name, u.family_name].filter(Boolean).join(' ') || u.username;
    const roles = (u.groups ?? []).filter((g) => g.startsWith('business')).map((g) => g.replace('business_', ''));
    const known = (STATUS_KEYS as readonly string[]).includes(u.status);
    return {
      key: u.username,
      title: name,
      sub: u.email,
      extra: roles.length ? roles.join(' · ') : undefined,
      avatar: (
        <span aria-hidden className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#E7F0EE] text-sm font-bold text-[var(--tenant-primary)]">
          {initialsOf(name)}
        </span>
      ),
      status: !u.enabled
        ? { label: t('phoneLists.adminUsers.disabled'), tone: 'bad' as Tone }
        : { label: known ? t(`phoneLists.adminUsers.status.${u.status}` as 'phoneLists.adminUsers.status.CONFIRMED') : u.status, tone: STATUS_TONE[u.status] ?? 'off' },
      open: () => onOpen(u),
    };
  });
  return (
    <PhonePage>
      <PhoneTitleRow title={title} onNew={onCreate} newLabel={t('phoneLists.adminUsers.create')} />
      <PhoneSearch placeholder={t('phoneLists.adminUsers.search')} value={query} onChange={onQuery} />
      <PhoneCapsules ariaLabel={title} chips={chips} onPick={onChip} />
      <div className="flex items-center justify-between gap-2 text-[13px] text-[var(--ink-500)]">
        <span>{loading ? '' : t('phoneLists.adminUsers.summary', { n: users.length.toLocaleString('en-US') })}</span>
        <button
          type="button"
          onClick={onOtherRole}
          className="-mr-2 h-11 cursor-pointer whitespace-nowrap border-0 bg-transparent px-2 font-[inherit] text-sm font-semibold text-[var(--tenant-primary,#005488)]"
        >
          {t('phoneLists.adminUsers.otherRole')}
        </button>
      </div>
      <PhoneListState loading={loading} failed={false} empty={!loading && items.length === 0} emptyText={t('phoneLists.adminUsers.empty')} />
      {!loading && items.length > 0 && <PhoneItemList items={items} />}
      {!loading && <PhonePager page={page} limit={ADMIN_USERS_PHONE_LIMIT} total={users.length} onPage={onPage} />}
    </PhonePage>
  );
};
